<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import {
    useMatterContextModalStore,
    isEventFieldModified,
    isEventModified,
} from '../../stores/matterContextModalStore';
import { useResizableColumn } from '../../composables/useResizableColumn';
import { useTableSort } from '../../composables/useTableSort';
import { formatEventDate } from '../../utils/eventDate';
import EventProfileDialog from './EventProfileDialog.vue';
import ContextMenu, { type ContextMenuItem } from '../common/ContextMenu.vue';
import { MATTER_CONTEXT_ROW_EXPANSION } from '../../config/featureFlags';

const { t } = useI18n();
const store = useMatterContextModalStore();

// Display-only sorting on Date. Parseable dates sort chronologically;
// unparseable date strings fall back to a string compare and sort last.
const parseDate = (value: string): number | null => {
    const t = Date.parse(value);
    return Number.isNaN(t) ? null : t;
};
const {
    sortedRows: sortedEvents,
    toggle: toggleSort,
    sortState,
    ariaSort,
} = useTableSort(() => store.events, {
    date: (a, b) => {
        const ta = parseDate(a.date);
        const tb = parseDate(b.date);
        if (ta === null && tb === null) return a.date.localeCompare(b.date);
        if (ta === null) return 1;
        if (tb === null) return -1;
        return ta - tb;
    },
});

const sortIcon = (key: string) => {
    const dir = sortState(key);
    return dir === 'asc' ? 'arrow_upward' : dir === 'desc' ? 'arrow_downward' : 'unfold_more';
};

// Sub-header text: the date range covered by the included (non-deleted) events
// that carry a parseable date. Events with prose/range dates (unparseable) are
// excluded from the span. Falls back to a generic prompt when none are dated.
const eventsSpanLabel = computed(() => {
    const dated = store.events
        .filter((e) => !e.deleted)
        .map((e) => ({ ts: parseDate(e.date), date: e.date }))
        .filter((e): e is { ts: number; date: string } => e.ts !== null);
    if (dated.length === 0) return t('matterContext.events.spanFallback');

    let min = dated[0];
    let max = dated[0];
    for (const e of dated) {
        if (e.ts < min.ts) min = e;
        if (e.ts > max.ts) max = e;
    }
    const minLabel = formatEventDate(min.date);
    const maxLabel = formatEventDate(max.date);
    if (minLabel === maxLabel) return t('matterContext.events.spanSingle', { date: minLabel });
    return t('matterContext.events.spanRange', { from: minLabel, to: maxLabel });
});

// Resizable Date column (Context fills the rest). Width is applied as the
// --mc-date-w CSS var on the table. Fixed columns: chevron (32, only when
// row expansion is enabled) + action (80).
const {
    tableEl,
    width: dateWidth,
    startResize: startDateResize,
} = useResizableColumn({ fixedColumnsWidth: (MATTER_CONTEXT_ROW_EXPANSION ? 32 : 0) + 80 });

// Accordion expand + right-side event detail dialog (mirrors the entities tab).
const expandedId = ref<string | null>(null);

const toggleExpand = (id: string) => {
    expandedId.value = expandedId.value === id ? null : id;
};

// `dialogCreateNew` flips the dialog into "new event" mode (a local draft, no
// store row until confirmed) so adding an event reuses the full edit-field set
// — the native date picker, required-date validation — via the same dialog.
// Add is reachable from the empty state and the table's "+ Add event" trigger;
// both call openCreate.
const dialogVisible = ref(false);
const dialogEventId = ref<string | null>(null);
const dialogCreateNew = ref(false);

const openDetails = (id: string) => {
    dialogCreateNew.value = false;
    dialogEventId.value = id;
    expandedId.value = id;
    dialogVisible.value = true;
};

const openCreate = () => {
    dialogCreateNew.value = true;
    dialogEventId.value = null;
    expandedId.value = null;
    dialogVisible.value = true;
};

const onDialogNavigate = (id: string) => {
    dialogEventId.value = id;
    expandedId.value = id;
};

const closeDetails = () => {
    dialogVisible.value = false;
    dialogCreateNew.value = false;
};

// Row-cell click action, feature-flagged: with row expansion enabled a click
// accordion-expands the inline preview (legacy behavior); with it disabled
// (default) the chevron column and preview don't render at all and a click
// opens the edit dialog directly. expandedId still tracks the dialog's
// current row either way (row highlight + scroll centering).
const onRowClick = (id: string) => {
    if (MATTER_CONTEXT_ROW_EXPANSION) toggleExpand(id);
    else openDetails(id);
};

// Per-row include/exclude switch. ON = included in searches/responses;
// toggling OFF soft-deletes the row (excluded), toggling back ON restores it.
// Blur the input right after toggling so its styled tooltip (shown on
// :focus-within) doesn't stick open once the pointer leaves.
const toggleIncluded = (row: { id: string; deleted?: boolean }, event: Event) => {
    if (row.deleted) store.restoreEvent(row.id);
    else store.deleteEvent(row.id);
    (event.target as HTMLElement | null)?.blur();
};

// Per-row kebab menu (to the right of the switch). Anchored to the icon's
// bottom-right; the shared ContextMenu clamps it into the viewport. Items
// re-trigger existing row actions (open the edit dialog / toggle active).
const rowMenu = ref<{ x: number; y: number; id: string } | null>(null);

const openRowMenu = (event: MouseEvent | KeyboardEvent, id: string) => {
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    rowMenu.value = { x: rect.right, y: rect.bottom + 4, id };
};

const closeRowMenu = () => {
    rowMenu.value = null;
};

const rowMenuItems = computed<ContextMenuItem[]>(() => {
    const id = rowMenu.value?.id;
    const row = id ? store.events.find((e) => e.id === id) : undefined;
    if (!row) return [];
    return [
        { label: t('matterContext.events.menuEdit'), icon: 'edit', onSelect: () => openDetails(row.id) },
        row.deleted
            ? {
                  label: t('matterContext.events.menuMakeActive'),
                  icon: 'check_circle',
                  onSelect: () => store.restoreEvent(row.id),
              }
            : { label: t('matterContext.events.menuMakeInactive'), icon: 'block', onSelect: () => store.deleteEvent(row.id) },
    ];
});

// Center the synced row so its expanded details stay visible as the dialog
// navigates a long list.
watch(expandedId, (id) => {
    if (!id) return;
    nextTick(() => {
        const row = tableEl.value?.querySelector<HTMLElement>(`[data-row-id="${id}"]`);
        row?.scrollIntoView({ block: 'center' });
    });
});
</script>

<template>
    <div v-if="store.events.length === 0" class="mc-tab-empty">
        <span class="material-icons mc-tab-empty-icon" aria-hidden="true">event_busy</span>
        <p class="mc-tab-empty-title">{{ t('matterContext.events.emptyTitle') }}</p>
        <p class="mc-tab-empty-text">
            {{ t('matterContext.events.emptyText') }}
        </p>
        <button type="button" class="mc-tab-empty-add" @click="openCreate">
            <span class="material-icons mc-add-icon">add</span>
            <span>{{ t('matterContext.events.addEvent') }}</span>
        </button>
    </div>

    <template v-else>
        <div class="mc-tab-confirm-bar">
            <span class="mc-tab-confirm-instruction">{{ eventsSpanLabel }}</span>
            <!-- The "Confirm events" button is commented out (may be revived later).
             The "Add event" trigger now lives in its place. -->
            <!-- <button
            type="button"
            class="mc-tab-confirm-btn"
            :class="{ 'mc-tab-confirm-btn-done': store.eventsConfirmed }"
            :disabled="!store.canConfirmEvents && !store.eventsConfirmed"
            @click="store.confirmEvents()"
        >
            <span
                v-if="store.eventsConfirmed"
                class="material-icons mc-tab-confirm-icon"
                aria-hidden="true"
                >check</span
            >
            {{ store.eventsConfirmed ? 'Events confirmed' : 'Confirm events' }}
        </button> -->
            <button type="button" class="mc-tab-add-btn" @click="openCreate">
                <span class="material-icons mc-add-icon">add</span>
                <span>{{ t('matterContext.events.addEvent') }}</span>
            </button>
        </div>

        <div
            ref="tableEl"
            class="mc-table"
            :class="{ 'mc-table-no-expand': !MATTER_CONTEXT_ROW_EXPANSION }"
            :style="{ '--mc-date-w': dateWidth + 'px' }"
        >
            <div class="mc-table-header">
                <div v-if="MATTER_CONTEXT_ROW_EXPANSION" class="mc-table-col mc-col-chevron"></div>
                <div
                    class="mc-table-col mc-col-date mc-col-resizable mc-col-sortable"
                    :aria-sort="ariaSort('date')"
                    @click="toggleSort('date')"
                >
                    {{ t('matterContext.events.colDate') }}
                    <span
                        class="material-icons mc-sort-icon"
                        :class="{ 'mc-sort-active': sortState('date') }"
                        aria-hidden="true"
                        >{{ sortIcon('date') }}</span
                    >
                    <span
                        class="mc-col-resize-handle"
                        :title="t('matterContext.common.dragToResize')"
                        @mousedown="startDateResize"
                        @click.stop
                    ></span>
                </div>
                <div class="mc-table-col mc-col-context">{{ t('matterContext.events.colContext') }}</div>
                <div class="mc-table-col mc-col-action"></div>
            </div>

            <template v-for="row in sortedEvents" :key="row.id">
                <div
                    class="mc-table-row"
                    :data-row-id="row.id"
                    :class="{
                        'mc-row-suggestion': row.suggestionStatus === 'pending',
                        'mc-row-accepted': row.suggestionStatus !== 'pending' && !row.deleted,
                        'mc-row-selected': expandedId === row.id,
                        'mc-row-deleted': row.deleted,
                    }"
                >
                    <div v-if="MATTER_CONTEXT_ROW_EXPANSION" class="mc-table-col mc-col-chevron">
                        <span
                            class="material-icons mc-row-chevron mc-row-chevron-active"
                            :class="{ 'mc-row-chevron-open': expandedId === row.id }"
                            :title="expandedId === row.id ? t('matterContext.common.collapse') : t('matterContext.common.expand')"
                            role="button"
                            tabindex="0"
                            @click.stop="toggleExpand(row.id)"
                            @keydown.enter.stop="toggleExpand(row.id)"
                            >expand_more</span
                        >
                    </div>
                    <div
                        class="mc-table-col mc-col-date mc-col-expandable"
                        @click="onRowClick(row.id)"
                    >
                        <span :class="{ 'mc-value-modified': isEventFieldModified(row, 'date') }">{{
                            formatEventDate(row.date) || '—'
                        }}</span>
                    </div>
                    <div
                        class="mc-table-col mc-col-context mc-col-expandable"
                        @click="onRowClick(row.id)"
                    >
                        <span
                            :class="{ 'mc-value-modified': isEventFieldModified(row, 'context') }"
                            >{{ row.context }}</span
                        >
                    </div>
                    <div class="mc-table-col mc-col-action">
                        <span
                            v-if="expandedId !== row.id && isEventModified(row)"
                            class="mc-row-mod-dot"
                            :title="t('matterContext.events.unsavedChanges')"
                        ></span>
                        <label class="mc-switch-wrap" @click.stop>
                            <span class="mc-switch">
                                <input
                                    type="checkbox"
                                    class="mc-switch-input"
                                    :checked="!row.deleted"
                                    @change="toggleIncluded(row, $event)"
                                />
                                <span class="mc-switch-track">
                                    <span class="mc-switch-thumb"></span>
                                </span>
                            </span>
                            <span class="mc-switch-tooltip" role="tooltip">
                                <!-- eslint-disable-next-line vue/no-v-html -- static i18n string with a bolded action word, no user data -->
                                <span v-html="t(row.deleted ? 'matterContext.events.tooltipExclude' : 'matterContext.events.tooltipInclude')"></span>
                            </span>
                        </label>
                        <span
                            class="material-icons mc-row-menu"
                            :title="t('matterContext.common.moreActions')"
                            role="button"
                            tabindex="0"
                            @click.stop="openRowMenu($event, row.id)"
                            @keydown.enter.stop="openRowMenu($event, row.id)"
                            >more_vert</span
                        >
                    </div>
                </div>

                <div
                    v-if="MATTER_CONTEXT_ROW_EXPANSION && expandedId === row.id"
                    class="mc-row-expanded mc-row-expanded-events"
                >
                    <div class="mc-row-expanded-fields">
                        <div class="mc-row-expanded-field">
                            <span class="mc-row-expanded-label">{{ t('matterContext.events.colDate') }}</span>
                            <span
                                class="mc-row-expanded-value"
                                :class="{
                                    'mc-row-expanded-empty': !row.date,
                                    'mc-value-modified':
                                        row.date && isEventFieldModified(row, 'date'),
                                }"
                            >
                                {{ formatEventDate(row.date) || t('matterContext.common.notSet') }}
                            </span>
                        </div>
                        <div class="mc-row-expanded-field">
                            <span class="mc-row-expanded-label">{{ t('matterContext.events.colContext') }}</span>
                            <span
                                class="mc-row-expanded-value"
                                :class="{
                                    'mc-row-expanded-empty': !row.context,
                                    'mc-value-modified':
                                        row.context && isEventFieldModified(row, 'context'),
                                }"
                            >
                                {{ row.context || t('matterContext.common.notSet') }}
                            </span>
                        </div>
                    </div>
                    <button type="button" class="mc-view-details-btn" @click="openDetails(row.id)">
                        {{ t('matterContext.common.viewAndEditAllDetails') }}
                    </button>
                </div>
            </template>
        </div>
    </template>

    <EventProfileDialog
        :visible="dialogVisible"
        :current-id="dialogEventId"
        :create-new="dialogCreateNew"
        @update:current-id="onDialogNavigate"
        @close="closeDetails"
    />

    <ContextMenu :position="rowMenu" :items="rowMenuItems" @close="closeRowMenu" />
</template>
